import { FunctionComponent, useRef, useCallback, useEffect, useMemo } from "react"
import { useHistory, RouteComponentProps, useParams } from "react-router-dom"
import IPage from "interfaces/IPage"
import { ISearchResults } from "interfaces/ISearchResults"
import useGetSgsByStatus from "hooks/useGetSgsByStatus"
import { EShippinggroupStatus } from "enums/shippinggroupEmun"

import OrderList from "overrides/theme/wom/base/components/Core/Order/OrderList"
import ErrorHandled from "components/Commons/Error/ErrorHandler"
import Paginate from "components/Commons/Pagination/Paginate"
import { OptionsFilterEnum } from "enums/OptionsFilterEnum"
import OrderSearchForm from "overrides/theme/wom/base/Pages/OrderSearch/OrderSearchForm"
import OrderContainerStyled from "components/Core/Order/OrderList.styled"
import {
  OrderSearchContainer,
  Title,
  LoadingOverlay,
  SpinnerRing,
  LoadingTextWrapper,
  LoadingTitle,
  LoadingSubtitle,
  ProgressBarTrack,
  ProgressBarFill,
} from "overrides/theme/wom/base/Pages/OrderSearch/OrderSearchScreen.styled"

const OrderSearchResultsViewer = ({ query, optionFilter }: { query: string, optionFilter: string }) => {
  const useGetOrderByStatus = useGetSgsByStatus()
  const refElemOrders = useRef<HTMLDivElement>(null)

  const optionfilter = optionFilter ? OptionsFilterEnum[optionFilter as keyof typeof OptionsFilterEnum] : undefined
  const filterOrder = useMemo(() => {
    return optionfilter ? { [optionfilter]: query } : (query ? { [OptionsFilterEnum.query]: query } : undefined)
  }, [optionfilter, query])

  const { ordersGroup, loading, maxPage, setPage, page, setSearch } = useGetOrderByStatus(
    EShippinggroupStatus.all.toString(),
    filterOrder || {},
  )

  useEffect(() => {
    if (setSearch && filterOrder) {
      setSearch(filterOrder)
    }
  }, [filterOrder, setSearch])

  const handleClickPage = (pageSeleted: number) => {
    setPage(pageSeleted)
  }

  return (
    <OrderContainerStyled>
      {loading ? (
        <LoadingOverlay>
          <SpinnerRing />
          <LoadingTextWrapper>
            <LoadingTitle>Buscando órdenes…</LoadingTitle>
            <LoadingSubtitle>Estamos consultando la información, esto puede tomar unos segundos.</LoadingSubtitle>
          </LoadingTextWrapper>
          <ProgressBarTrack>
            <ProgressBarFill />
          </ProgressBarTrack>
        </LoadingOverlay>
      ) : (
        <>
          {ordersGroup ? (
            <>
              <OrderList ordersGroup={ordersGroup} showStatus basePath={`/view_orders`} />
              {maxPage > 1 && (
                <Paginate
                  pageCount={maxPage}
                  onChangePage={handleClickPage}
                  elementFocus={refElemOrders}
                  pageInit={page}
                />
              )}
            </>
          ) : (
            <ErrorHandled message="No se encontraron órdenes asociadas a la búsqueda ingresada." />
          )}
        </>
      )}
    </OrderContainerStyled>
  )
}

const OrderSearchScreen: FunctionComponent<IPage & RouteComponentProps<ISearchResults>> = () => {
  const { query, optionFilter }: any = useParams()
  const history = useHistory()

  const hasQuery = !!query

  const handleClickSearch = useCallback(
    ({ query: newQuery, optionFilter: newOptionFilter }: ISearchResults) => {
      if (newQuery && newOptionFilter) {
        history.push(`/view/orders/${newQuery}/${newOptionFilter}`)
      } else if (newQuery) {
        history.push(`/view/orders/${newQuery}/query`)
      }
    },
    [history],
  )

  return (
    <OrderSearchContainer style={{ maxWidth: '100%', backgroundColor: 'transparent', boxShadow: 'none' }}>
      <Title>Consulta de Órdenes</Title>

      <OrderSearchForm
        search={query}
        optionFilter={optionFilter}
        onClickSearch={handleClickSearch}
        isLoading={false}
      />

      {hasQuery && <OrderSearchResultsViewer query={query} optionFilter={optionFilter} />}
    </OrderSearchContainer>
  )
}

export default OrderSearchScreen
